import {
  BrainBlueprintArea,
  BrainEntityOrigin,
  BrainEntityStatus,
  BrainEntityType,
  KnowledgeSpace,
} from '@this+that/models';

import { collectDescendantIds } from './treeDescendants';
import type {
  BrainEntityView,
  ConfirmBrainEntityInput,
} from '../../../../../../../../models/companyBrain';

export enum BrainEntityDisplayStatus {
  DECLARED = 'declared',
  DISCOVERED = 'discovered',
  TO_CONFIRM = 'toConfirm',
}

export interface BrainEntityPageLink {
  pageId: string;
  label: string | null;
}

export interface BrainEntityCandidate {
  entityId: string;
  name: string;
  type: BrainEntityType;
}

export interface ConfirmEntityDraft {
  name: string;
  type: BrainEntityType;
  parentEntityId: string | null;
}

export type ConfirmEntityResult =
  | { input: ConfirmBrainEntityInput; errorKey: null }
  | { input: null; errorKey: string };

const WHITESPACE_RUN = /\s+/g;
const SINGLE_SPACE = ' ';
const NAME_REQUIRED_KEY = 'brain.blueprint.entities.confirm.nameRequired';

export function resolveEntityDisplayStatus(
  entity: Pick<BrainEntityView, 'origin' | 'status'>,
): BrainEntityDisplayStatus {
  if (entity.status === BrainEntityStatus.NEEDS_CONFIRMATION) {
    return BrainEntityDisplayStatus.TO_CONFIRM;
  }
  return entity.origin === BrainEntityOrigin.DECLARED
    ? BrainEntityDisplayStatus.DECLARED
    : BrainEntityDisplayStatus.DISCOVERED;
}

export function entityChildren(entity: BrainEntityView): BrainEntityView[] {
  return entity.children ?? [];
}

function resolvePageLabel(
  spaceId: string,
  areas: readonly BrainBlueprintArea[],
  spaces: readonly KnowledgeSpace[],
): string | null {
  const area = areas.find((candidate) => candidate.spaceId === spaceId);
  if (area) {
    return area.name;
  }
  return spaces.find((space) => space.spaceId === spaceId)?.name ?? null;
}

export function buildEntityPageLinks(
  entity: Pick<BrainEntityView, 'projectionPages'>,
  areas: readonly BrainBlueprintArea[],
  spaces: readonly KnowledgeSpace[],
): BrainEntityPageLink[] {
  const seen = new Set<string>();
  return entity.projectionPages.flatMap(({ spaceId, pageId }) => {
    if (seen.has(pageId)) {
      return [];
    }
    seen.add(pageId);
    return [{ pageId, label: resolvePageLabel(spaceId, areas, spaces) }];
  });
}

function collectEntities(
  entities: readonly BrainEntityView[],
  seen: Set<string>,
  out: BrainEntityView[],
): void {
  for (const entity of entities) {
    if (
      entity.status === BrainEntityStatus.MERGED ||
      seen.has(entity.entityId)
    ) {
      continue;
    }
    seen.add(entity.entityId);
    out.push(entity);
    collectEntities(entityChildren(entity), seen, out);
  }
}

export function withoutPersonEntities(
  entities: readonly BrainEntityView[],
): BrainEntityView[] {
  return entities.flatMap((entity) =>
    entity.type === BrainEntityType.PERSON
      ? withoutPersonEntities(entityChildren(entity))
      : [entity],
  );
}

export function flattenEntityTree(
  roots: readonly BrainEntityView[],
): BrainEntityView[] {
  const out: BrainEntityView[] = [];
  collectEntities(roots, new Set(), out);
  return out;
}

export function buildEntityCandidateOptions(
  entityId: string,
  entities: readonly BrainEntityView[],
): BrainEntityCandidate[] {
  const excluded = collectDescendantIds(
    entityId,
    entities.map((entity) => ({
      id: entity.entityId,
      parentId: entity.parentEntityId,
    })),
  );
  return entities
    .filter(
      (entity) =>
        !excluded.has(entity.entityId) &&
        entity.status !== BrainEntityStatus.MERGED,
    )
    .map((entity) => ({
      entityId: entity.entityId,
      name: entity.canonicalName,
      type: entity.type,
    }));
}

function normalizeEntityName(name: string): string {
  return name.trim().replace(WHITESPACE_RUN, SINGLE_SPACE);
}

export function buildConfirmEntityInput(
  entity: Pick<BrainEntityView, 'canonicalName' | 'type' | 'parentEntityId'>,
  draft: ConfirmEntityDraft,
): ConfirmEntityResult {
  const name = normalizeEntityName(draft.name);
  if (!name) {
    return { input: null, errorKey: NAME_REQUIRED_KEY };
  }
  const input: ConfirmBrainEntityInput = {};
  if (name !== entity.canonicalName) {
    input.name = name;
  }
  if (draft.type !== entity.type) {
    input.type = draft.type;
  }
  if (draft.parentEntityId !== (entity.parentEntityId ?? null)) {
    input.parentEntityId = draft.parentEntityId;
  }
  return { input, errorKey: null };
}
